import { useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { Droplet, Thermometer, Waves, Zap } from "lucide-react";

import { useArchiveGrow, useGrow } from "@/lib/api/grows";
import { useGrowReadings, useLatestReading } from "@/lib/api/grow-readings";
import { usePendingActionsForGrow } from "@/lib/api/pending-actions";
import {
  latestReservoirState,
  useRefillSessions,
} from "@/lib/api/refill-sessions";
import { useReservoir } from "@/lib/api/reservoirs";
import type {
  Grow,
  GrowReading,
  PendingAction,
  RefillSession,
} from "@/lib/api/types";
import { usePreferences } from "@/lib/preferences";
import {
  ecFromMsCm,
  ecUnitLabel,
  formatEc,
  formatTemp,
  tempFromCelsius,
  tempUnitLabel,
} from "@/lib/units";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ErrorState } from "@/components/error-state";
import { AuditLogViewer } from "@/components/audit-log-viewer";
import { UrgencyBadge } from "@/components/urgency-badge";
import { GrowHistoryChart } from "@/components/dashboard/grow-history-chart";
import { Metric } from "@/components/metric";
import { ReadingCaptureCard } from "@/components/grows/reading-capture-card";
import { CaptureSelectionProvider } from "@/lib/capture/selection";
import { GrowDiagnosePanel } from "./diagnose";

// The grow detail page is the per-grow hub. The header carries identity +
// lifecycle actions (edit / archive); below it a tab strip lets the grower
// drill into current chemistry, open actions, refill history, the audit
// trail, or the AI analyze panel without one flat scroll of everything.
export function GrowDetailPage() {
  const { growId } = useParams({ from: "/grows/$growId" });
  const navigate = useNavigate();
  const q = useGrow(growId);
  const archive = useArchiveGrow(growId);

  if (q.isLoading)
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (q.error) return <ErrorState error={q.error} retry={q.refetch} />;
  if (!q.data) return null;
  const g = q.data;

  function onArchive() {
    const reason =
      window.prompt("Reason for archiving this grow?") || "user archive";
    archive.mutate(reason, {
      onSuccess: () => navigate({ to: "/grows" }),
    });
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">{g.species}</h1>
          <p className="text-sm text-muted-foreground">
            {g.system_type} · {g.count} plant{g.count === 1 ? "" : "s"} · state{" "}
            <span className="font-medium">{g.current_state}</span>
            {g.archived_at && " · archived"}
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/grows/$growId/edit" params={{ growId: g.id }}>
            <Button variant="outline">Edit</Button>
          </Link>
          {!g.archived_at && (
            <Button
              variant="ghost"
              onClick={onArchive}
              disabled={archive.isPending}
            >
              {archive.isPending ? "Archiving…" : "Archive"}
            </Button>
          )}
        </div>
      </header>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="readings">Readings</TabsTrigger>
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="refills">Refills</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
          <TabsTrigger value="analyze">Analyze</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab grow={g} />
        </TabsContent>
        <TabsContent value="readings">
          <ReadingsTab grow={g} />
        </TabsContent>
        <TabsContent value="pending">
          <PendingTab grow={g} />
        </TabsContent>
        <TabsContent value="refills">
          <RefillsTab grow={g} />
        </TabsContent>
        <TabsContent value="activity">
          <section className="space-y-2">
            <h2 className="text-sm font-medium tracking-wide text-muted-foreground uppercase">
              Audit trail
            </h2>
            <AuditLogViewer entityType="grow" entityID={g.id} />
          </section>
        </TabsContent>
        <TabsContent value="analyze">
          <GrowDiagnosePanel grow={g} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------- Overview ----------

// OverviewTab pulls the latest refill session to source the "current"
// reservoir reading and renders it against the grow's ideal band, then shows
// the target band, the bound reservoir, and any notes.
function OverviewTab({ grow }: { grow: Grow }) {
  const { ecUnit, temperatureUnit } = usePreferences();
  const sessions = useRefillSessions({ grow_id: grow.id, limit: 20 });
  const reservoir = useReservoir(grow.reservoir_id);
  const latestReadingQ = useLatestReading(grow.id);
  const [takingReading, setTakingReading] = useState(false);

  const latest = sessions.data?.items[0];
  const state = latestReservoirState(latest);
  const lastRefillAt = latest?.closed_at ?? latest?.started_at;

  // "Current readings" reflects the most recent of {latest saved reading,
  // latest refill-session state} (issue 4: a saved pen reading updates the
  // panel). pH/EC/temp come from whichever is newer; level prefers the current
  // reading when it recorded one (manual entry), otherwise the latest refill.
  const reading = latestReadingQ.data ?? null;
  const readingTime = reading ? Date.parse(reading.captured_at) : -Infinity;
  const stateTime = state?.captured_at
    ? Date.parse(state.captured_at)
    : lastRefillAt
      ? Date.parse(lastRefillAt)
      : -Infinity;
  const useReading = !!reading && readingTime >= stateTime;
  // Per-field precedence: a newer reading wins for each metric it actually
  // recorded, but a metric it left blank (a reading can be level-only or
  // notes-only) falls back to the latest refill state rather than showing "—".
  const cur = {
    ph: useReading && reading!.ph != null ? reading!.ph : state?.ph,
    ec:
      useReading && reading!.ec_ms_per_cm != null
        ? reading!.ec_ms_per_cm
        : state?.ec,
    temp_c:
      useReading && reading!.temp_c != null ? reading!.temp_c : state?.temp_c,
    level_gal:
      useReading && reading!.level_gal != null
        ? reading!.level_gal
        : state?.level_gal,
  };
  const currentAt = useReading
    ? reading!.captured_at
    : (state?.captured_at ?? lastRefillAt);
  const currentSource = useReading ? "reading" : lastRefillAt ? "refill" : null;
  const loadingCurrent = sessions.isLoading || latestReadingQ.isLoading;

  return (
    <div className="space-y-6">
      <section className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-medium tracking-wide text-muted-foreground uppercase">
            Current readings
          </h2>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {loadingCurrent
                ? "Loading…"
                : currentAt
                  ? `as of ${formatDistanceToNow(new Date(currentAt), {
                      addSuffix: true,
                    })}${currentSource ? ` · ${currentSource}` : ""}`
                  : "no readings yet"}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setTakingReading((v) => !v)}
              aria-expanded={takingReading}
            >
              {takingReading ? "Hide" : "Take reading"}
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Metric
            icon={<Droplet className="size-4" aria-hidden />}
            label="pH"
            current={cur.ph}
            ideal={grow.ph_ideal}
            min={grow.ph_min}
            max={grow.ph_max}
            digits={2}
          />
          <Metric
            icon={<Zap className="size-4" aria-hidden />}
            label={ecUnitLabel(ecUnit)}
            current={cur.ec}
            ideal={grow.ec_ideal}
            min={grow.ec_min}
            max={grow.ec_max}
            display={(n) => ecFromMsCm(n, ecUnit)}
            digits={ecUnit === "ppm700" ? 0 : 2}
          />
          <Metric
            icon={<Thermometer className="size-4" aria-hidden />}
            label={`Temp ${tempUnitLabel(temperatureUnit)}`}
            current={cur.temp_c}
            ideal={grow.temp_ideal_c}
            min={grow.temp_min_c}
            max={grow.temp_max_c}
            display={(n) => tempFromCelsius(n, temperatureUnit)}
            digits={1}
          />
          <Metric
            icon={<Waves className="size-4" aria-hidden />}
            label="Level"
            current={cur.level_gal}
            suffix=" gal"
            digits={1}
          />
        </div>
        {/* Capture from the pen (or type) and save straight to this grow; the
            card's ✓ Save writes to the log and the precedence above then shows
            it as the current reading. The provider arms the `c` shortcut. */}
        {takingReading && (
          <CaptureSelectionProvider>
            <ReadingCaptureCard grow={grow} />
          </CaptureSelectionProvider>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-medium tracking-wide text-muted-foreground uppercase">
          Target band <span className="normal-case">(min · ideal · max)</span>
        </h2>
        <div className="grid gap-4 md:grid-cols-3">
          <ChemistryCard
            title="pH"
            min={grow.ph_min}
            ideal={grow.ph_ideal}
            max={grow.ph_max}
          />
          <ChemistryCard
            title={`EC (${ecUnit === "ppm700" ? "ppm" : "mS/cm"})`}
            min={grow.ec_min}
            ideal={grow.ec_ideal}
            max={grow.ec_max}
            format={(n) => formatEc(n, ecUnit)}
          />
          <ChemistryCard
            title={`Temp (${tempUnitLabel(temperatureUnit)})`}
            min={grow.temp_min_c}
            ideal={grow.temp_ideal_c}
            max={grow.temp_max_c}
            format={(n) => formatTemp(n, temperatureUnit)}
          />
        </div>
      </section>

      {grow.reservoir_id && (
        <section className="space-y-1">
          <h2 className="text-sm font-medium tracking-wide text-muted-foreground uppercase">
            Reservoir
          </h2>
          {reservoir.data ? (
            <Link
              to="/reservoirs/$reservoirId"
              params={{ reservoirId: grow.reservoir_id }}
              className="text-sm font-medium hover:underline"
            >
              {reservoir.data.name}
            </Link>
          ) : (
            <p className="text-sm text-muted-foreground">
              {reservoir.isLoading ? "Loading…" : grow.reservoir_id}
            </p>
          )}
        </section>
      )}

      {grow.notes && (
        <section className="space-y-1">
          <h2 className="text-sm font-medium tracking-wide text-muted-foreground uppercase">
            Notes
          </h2>
          <p className="text-sm whitespace-pre-wrap">{grow.notes}</p>
        </section>
      )}
    </div>
  );
}

// ---------- Readings ----------

// ReadingsTab is the grow's running log of saved chemistry readings
// (newest-first), each with its values, how it was captured, notes, and the
// attached AI assessment when the reading was saved together with one.
function ReadingsTab({ grow }: { grow: Grow }) {
  const { ecUnit, temperatureUnit } = usePreferences();
  const q = useGrowReadings(grow.id);
  const items = q.data?.items ?? [];

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-medium tracking-wide text-muted-foreground uppercase">
          Readings log
        </h2>
        <span className="text-xs text-muted-foreground">
          {items.length} saved
        </span>
      </div>

      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading readings…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No readings yet. Take one from the Overview or Analyze tab — it’ll
          show up here as a running log.
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map((r) => (
            <ReadingRow
              key={r.id}
              reading={r}
              ecUnit={ecUnit}
              temperatureUnit={temperatureUnit}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function ReadingRow({
  reading,
  ecUnit,
  temperatureUnit,
}: {
  reading: GrowReading;
  ecUnit: ReturnType<typeof usePreferences>["ecUnit"];
  temperatureUnit: ReturnType<typeof usePreferences>["temperatureUnit"];
}) {
  return (
    <li className="rounded-md border px-3 py-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-sm font-medium tabular-nums">
          {new Date(reading.captured_at).toLocaleString(undefined, {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
          })}
        </span>
        <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
          {reading.source === "pen" ? "🖊 pen" : "✍️ manual"}
        </span>
        <span className="ml-auto flex items-baseline gap-3 text-sm tabular-nums">
          <span>pH {phStr(reading.ph)}</span>
          <span>
            {ecUnitLabel(ecUnit)} {formatEc(reading.ec_ms_per_cm, ecUnit)}
          </span>
          <span>
            {tempUnitLabel(temperatureUnit)}{" "}
            {formatTemp(reading.temp_c, temperatureUnit, 1)}
          </span>
        </span>
      </div>
      {reading.notes && (
        <p className="mt-1 whitespace-pre-wrap text-xs text-muted-foreground">
          {reading.notes}
        </p>
      )}
      {reading.ai_summary && (
        <div className="mt-2 rounded-md bg-muted/40 p-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            ✨ AI analysis
          </p>
          <p className="mt-1 whitespace-pre-wrap text-sm">
            {reading.ai_summary}
          </p>
          {reading.ai_actions && reading.ai_actions.length > 0 && (
            <ul className="mt-2 space-y-1">
              {reading.ai_actions.map((a, i) => (
                <li key={i} className="flex items-center gap-2 text-xs">
                  <UrgencyBadge urgency={a.urgency} className="shrink-0" />
                  <span className="font-medium">{a.title}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
}

function phStr(n: number | undefined): string {
  return typeof n === "number" && Number.isFinite(n) ? n.toFixed(2) : "—";
}

// ---------- Pending ----------

// PendingTab is a read-only roll-up of this grow's open pending actions. The
// full management UI lives at /pending-actions; this just surfaces what's
// outstanding and links there pre-filtered to this grow.
function PendingTab({ grow }: { grow: Grow }) {
  const pending = usePendingActionsForGrow(grow.id);
  const items = pending.data?.items ?? [];

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-medium tracking-wide text-muted-foreground uppercase">
          Open actions
        </h2>
        <Link
          to="/pending-actions"
          search={{ grow: grow.id }}
          className="text-sm font-medium hover:underline"
        >
          Manage all →
        </Link>
      </div>

      {pending.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading actions…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">No pending actions.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((a) => (
            <PendingRow key={a.id} action={a} />
          ))}
        </ul>
      )}
    </section>
  );
}

function PendingRow({ action }: { action: PendingAction }) {
  return (
    <li className="flex items-start gap-2 rounded-md border px-3 py-2">
      <UrgencyBadge urgency={action.urgency} className="mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{action.title}</p>
        {action.rationale && (
          <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
            {action.rationale}
          </p>
        )}
      </div>
    </li>
  );
}

// ---------- Refills ----------

// RefillsTab shows the refill history (newest-first) as links into each
// session, plus the pH/EC trend sparkline and a shortcut to start a new
// refill pre-targeted at this grow.
function RefillsTab({ grow }: { grow: Grow }) {
  const sessions = useRefillSessions({ grow_id: grow.id });
  const items = sessions.data?.items ?? [];

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-medium tracking-wide text-muted-foreground uppercase">
          Refill history
        </h2>
        <Link to="/refills/new" search={{ grow: grow.id }}>
          <Button variant="outline" size="sm">
            Start refill
          </Button>
        </Link>
      </div>

      {items.length > 0 && <GrowHistoryChart sessions={items} />}

      {sessions.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading refills…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">No refills yet.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((s) => (
            <RefillRow key={s.id} session={s} />
          ))}
        </ul>
      )}
    </section>
  );
}

function RefillRow({ session }: { session: RefillSession }) {
  const status = session.voided_at
    ? "voided"
    : session.closed_at
      ? "closed"
      : "open";
  return (
    <li>
      <Link
        to="/refills/$sessionId"
        params={{ sessionId: session.id }}
        className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border px-3 py-2 text-sm transition-colors hover:bg-accent/50"
      >
        <span className="font-medium tabular-nums">
          {shortDate(session.started_at)}
        </span>
        {session.trigger && (
          <span className="text-muted-foreground">{session.trigger}</span>
        )}
        {session.decision_type && (
          <span className="text-muted-foreground">
            · {session.decision_type}
          </span>
        )}
        {session.recipe_version !== undefined && (
          <span className="text-xs text-muted-foreground">
            recipe v{session.recipe_version}
          </span>
        )}
        <span
          className={
            "ml-auto rounded px-1.5 py-0.5 text-xs " +
            (status === "voided"
              ? "bg-muted text-muted-foreground line-through"
              : status === "open"
                ? "bg-amber-500/15 text-amber-700 dark:text-amber-400"
                : "bg-muted text-muted-foreground")
          }
        >
          {status}
        </span>
      </Link>
    </li>
  );
}

function shortDate(ts: string): string {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// ---------- Shared building blocks ----------

function ChemistryCard({
  title,
  min,
  ideal,
  max,
  format = fmt,
}: {
  title: string;
  min?: number;
  ideal?: number;
  max?: number;
  format?: (n: number | undefined) => string;
}) {
  const empty = min === undefined && ideal === undefined && max === undefined;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {empty ? (
          <p className="text-sm text-muted-foreground">not set</p>
        ) : (
          <p className="text-base tabular-nums">
            <span className="text-muted-foreground">{format(min)}</span>
            {" · "}
            <span className="font-semibold">{format(ideal)}</span>
            {" · "}
            <span className="text-muted-foreground">{format(max)}</span>
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function fmt(n: number | undefined): string {
  if (n === undefined || Number.isNaN(n)) return "—";
  return String(n);
}
